// La navigation (D-139, maquettes n1 à n4 validées ; Idries : « il y a beaucoup d'imbrication de pages dans d'autres
// pages ») : logique pure, testée (nav.test.ts). Le mécanisme (historique, « ‹ X », ⚙) est dans src/ui/nav.ts.
//   * Un module = une page ; ses vues sont des segments en tête de page, qui remplacent l'adresse (jamais empilés).
//   * La ⚙ : à gauche du « ? », sur la page du module, pour qui peut régler ; elle ouvre UNE page de réglages en
//     rubriques repliables (`#/<module>/reglages[/<rubrique>]`).
//   * « ‹ X » dit d'où l'on vient et y ramène ; sans historique (notification, lien), le parent du module.
//   * Trois niveaux au plus : onglet › page › réglages ou fiche (un objet s'ouvre en feuille).
import type { ModuleKey } from './modules';

/** Les pages de réglages : un module (sauf Agenda, Défis, Prépa, Parrainage : rien à régler au niveau du club), plus les
 *  reçus fiscaux (une entrée de Bénévolat et Partenaires), et les dérogations (D-137 : le délai « sans réponse », du socle). */
export type SettingsKey = 'benevolat' | 'licences' | 'salles' | 'partenaires' | 'avantages' | 'subventions' | 'page' | 'recus' | 'derogations' | 'formation' | 'ressentis' | 'bilan' | 'tournois';

export interface SettingsInfo {
  /** L'adresse de la page de réglages (la ⚙ y mène). */
  href: string;
  /** Son nom, celui que dit le retour d'une page ouverte depuis elle : « ‹ Réglages des avantages ». */
  title: string;
  /** Le surtitre de la page (le module). */
  eyebrow: string;
  /** La page du module : son retour sans historique (« ‹ Licences »). */
  parent: { hash: string; label: string };
}

export const SETTINGS: Record<SettingsKey, SettingsInfo> = {
  benevolat: { href: '#/benevoles/reglages', title: 'Réglages du bénévolat', eyebrow: 'Bénévolat', parent: { hash: '#/benevoles', label: 'Bénévolat' } },
  licences: { href: '#/licences/reglages', title: 'Réglages des licences', eyebrow: 'Licences et dossiers', parent: { hash: '#/licences', label: 'Licences' } },
  salles: { href: '#/salles/reglages', title: 'Réglages des salles', eyebrow: 'Les salles', parent: { hash: '#/salles', label: 'Les salles' } },
  partenaires: { href: '#/partenaires/reglages', title: 'Réglages des partenaires', eyebrow: 'Partenaires', parent: { hash: '#/partenaires', label: 'Partenaires' } },
  avantages: { href: '#/avantages/reglages', title: 'Réglages des avantages', eyebrow: 'Avantages', parent: { hash: '#/avantages', label: 'Avantages' } },
  subventions: { href: '#/subventions/reglages', title: 'Réglages des subventions', eyebrow: 'Subventions', parent: { hash: '#/subventions', label: 'Subventions' } },
  page: { href: '#/club/page/reglages', title: 'Réglages de la page', eyebrow: 'Page du club', parent: { hash: '#/club/page', label: 'Page du club' } },
  recus: { href: '#/club/recus', title: 'Réglages des reçus', eyebrow: 'Reçus fiscaux', parent: { hash: '#/club/registre', label: 'Reçus fiscaux' } },
  derogations: { href: '#/derogations/reglages', title: 'Réglages des dérogations', eyebrow: 'Dérogations', parent: { hash: '#/derogations', label: 'Dérogations' } },
  // D-141 : la formation (bêta) ; la ⚙ de « Nos officiels ».
  formation: { href: '#/formation/reglages', title: 'Réglages de la formation', eyebrow: 'Formation · bêta', parent: { hash: '#/formation', label: 'Formation' } },
  // D-143 (socle) : les ressentis (le coach pour ses équipes, le bureau pour toutes) et le bilan de saison (le bureau).
  ressentis: { href: '#/ressentis/reglages', title: 'Réglages des ressentis', eyebrow: 'Ressentis', parent: { hash: '#/ressentis', label: 'Ressentis' } },
  bilan: { href: '#/bilan/reglages', title: 'Réglages du bilan', eyebrow: 'Bilan de saison', parent: { hash: '#/bilan/suivi', label: 'Bilan de saison' } },
  // D-175, D-177 : les tournois (les organisateurs, les coachs) ; la ⚙ des écrans d'un tournoi.
  tournois: { href: '#/tournoi/reglages', title: 'Réglages des tournois', eyebrow: 'Tournois', parent: { hash: '#/tournoi', label: 'Tournois' } },
};

/** Les modules qui ont des réglages (la roue de « Modules et réglages ») ; les autres n'en ont pas. */
export const MODULE_SETTINGS: Partial<Record<ModuleKey, SettingsKey>> = {
  salles: 'salles', benevolat: 'benevolat', licences: 'licences', partenaires: 'partenaires', avantages: 'avantages', page: 'page', subventions: 'subventions',
  formation: 'formation', tournois: 'tournois',
};

const split = (hash: string): string[] => hash.replace(/^#\/?/, '').split('?')[0]!.split('/').filter(Boolean);

/** La page d'un module qui porte la ⚙ (ses segments compris) : la clé de ses réglages, sinon null. */
export function settingsOfRoute(route: string[]): SettingsKey | null {
  const [a = '', b, c] = route;
  if (a === 'benevoles' && (!b || ['equipe', 'badges', 'points'].includes(b)) && !c) return 'benevolat';
  if (a === 'licences' && !b) return 'licences';
  // D-138 : la semaine du club (ses semaines) et la journée heure par heure ; D-144 : un lieu FFBB à relier (la semaine, sa feuille).
  if (a === 'salles' && (!b || b === 'semaine' || b === 'jour' || b === 'relier')) return 'salles';
  if ((a === 'partenaires' || a === 'lien') && !b) return 'partenaires';
  if (a === 'prospection' && (!b || b === 'suivi' || (b === 'kit' && !c))) return 'partenaires';
  if (a === 'avantages' && !b) return 'avantages';
  if (a === 'subventions' && (!b || b === 'calendrier' || b === 'dossiers')) return 'subventions';
  if (a === 'club' && b === 'registre' && !c) return 'recus';
  if (a === 'club' && b === 'page' && !c) return 'page';
  if (a === 'club' && b === 'site' && !c) return 'page';   // D-140 : le site du club, une page du module « Page du club »
  if (a === 'derogations' && !b) return 'derogations';
  if (a === 'formation' && (!b || b === 'valider' || b === 'officiels')) return 'formation';
  if (a === 'ressentis' && b !== 'reglages') return 'ressentis';
  if (a === 'bilan' && b === 'suivi') return 'bilan';
  if (a === 'tournoi' && b !== 'reglages' && b !== 'nouveau') return 'tournois';
  return null;
}

/** Une page de réglages (la ⚙ y mène) : sa clé et la rubrique demandée (`#/salles/reglages/gestionnaires`), sinon null. */
export function settingsPageOf(route: string[]): { key: SettingsKey; rubric: string | null; item: string | null } | null {
  const [a = '', b, c, d] = route;
  if (a === 'club' && b === 'recus') return { key: 'recus', rubric: c ?? null, item: null };
  if (a === 'club' && b === 'page' && c === 'reglages') return { key: 'page', rubric: d ?? null, item: null };
  if (b !== 'reglages') return null;
  const key = ({ benevoles: 'benevolat', licences: 'licences', salles: 'salles', partenaires: 'partenaires', avantages: 'avantages', subventions: 'subventions', derogations: 'derogations',
    formation: 'formation', ressentis: 'ressentis', bilan: 'bilan', tournoi: 'tournois' } as const)[a as 'benevoles'];
  return key ? { key, rubric: c ?? null, item: d ?? null } : null;
}

/** Les adresses d'avant D-139 (liens des notifications, liens déjà envoyés, favoris, visites d'avant) : leur nouvelle
 *  adresse, la page ou la rubrique qui les remplace, sans entrée d'historique de plus. */
export function movedNavRoute(hash: string): string | null {
  const h = hash.replace(/\/+$/, '');
  const r = split(h);
  const [a = '', b, c] = r;
  // Club › Bénévolat (D-127) et ses sous-pages : la page des réglages du bénévolat, la rubrique dépliée.
  if (a === 'club' && b === 'benevolat') return c ? `#/benevoles/reglages/${c}` : '#/benevoles/reglages';
  // (#/benevoles/matchs et #/benevoles/parcours : src/domain/reglages.ts, movedRoute.)
  // Salles (D-130) : les gestionnaires sont une rubrique des réglages ; un gestionnaire, sa feuille.
  if (a === 'salles' && b === 'gestionnaires' && !c) return '#/salles/reglages/gestionnaires';
  if (a === 'salles' && b === 'gestionnaire' && c) return `#/salles/reglages/gestionnaires/${c}`;
  // D-138 : « Nos créneaux · Liste » (ce qui était à traiter : la semaine du club, son à-faire ; la dotation : la ⚙) ; les
  // rubriques « Responsable des salles » et « Salles de réception » rejoignent « Règles » et « Salles et créneaux ».
  if (a === 'salles' && b === 'liste' && !c) return '#/salles';
  if (a === 'salles' && b === 'reglages' && c === 'responsable') return '#/salles/reglages/regles';
  if (a === 'salles' && b === 'reglages' && c === 'reception') return '#/salles/reglages/salles';
  // Avantages : « Gérer les avantages » devient leurs réglages.
  if (a === 'avantages' && b === 'gestion' && !c) return '#/avantages/reglages';
  // « Ce que le club offre » (Garder le lien) : une rubrique des réglages des partenaires.
  if (a === 'lien' && b === 'offres' && !c) return '#/partenaires/reglages/offres';
  // Subventions : le coffre des pièces et les chiffres passent sous la ⚙.
  if (a === 'subventions' && (b === 'pieces' || b === 'chiffres') && !c) return `#/subventions/reglages/${b}`;
  return null;
}

/** Les noms des pages que l'on connaît par leur adresse (le retour d'une page sans historique). */
const TITLES: Record<string, string> = {
  '#/': 'Accueil', '#/planning': 'Planning', '#/matchs': 'Matchs', '#/club': 'Le club', '#/moi': 'Moi', '#/licences': 'Licences',
  '#/benevoles': 'Bénévolat', '#/agenda': 'Agenda', '#/salles': 'Les salles', '#/partenaires': 'Partenaires', '#/avantages': 'Avantages',
  '#/subventions': 'Subventions', '#/subventions/dossiers': 'Subventions', '#/prospection': 'Partenaires', '#/prospection/suivi': 'Prospection',
  '#/lien': 'Garder le lien', '#/annonces': 'Annonces', '#/basket': 'Basket', '#/mains-vives': 'Mains vives', '#/notifs': 'Notifications', '#/aide': 'Aide', '#/enfants': 'Enfants',
  '#/club/registre': 'Reçus fiscaux', '#/derogations': 'Dérogations', '#/club/page': 'Page du club', '#/club/bureau': 'Le bureau', '#/club/modules': 'Modules et réglages',
  '#/marrainage': 'Parrainage des jeunes', '#/prepa': 'Prépa', '#/defis': 'Défis et gages', '#/membres': 'Membres et rôles', '#/console': 'Console',
  '#/support': 'Support', '#/badges': 'Mes badges', '#/moi/skins': 'Mes skins', '#/club/modules/skins': 'Les skins du club', '#/console/skins': 'Skins', '#/rt/joueurs': 'Joueurs', '#/rt/coachs': 'Coachs', '#/rt/equipes': 'Équipes',
  '#/formation': 'Formation', '#/formation/valider': 'Formation', '#/formation/officiels': 'Formation', '#/tournoi': 'Tournois',
  '#/ressentis': 'Ressentis', '#/bilan': 'Bilan de saison', '#/bilan/suivi': 'Bilan de saison', '#/nouvelle-saison': 'Nouvelle saison',
};
export const titleOfHash = (hash: string): string | null => { const h = hash.replace(/\/+$/, ''); return TITLES[h === '#' || !h ? '#/' : h] ?? null; };

/** Le parent du module, quand la page n'a pas d'historique (notification push, lien, app rouverte) : les pages de
 *  réglages mènent à la page de leur module, le reste à la page que nomme son propre retour (`fallback`), sinon à
 *  l'accueil. */
export function parentOf(route: string[], fallback: { hash: string; label: string } | null): { hash: string; label: string } {
  const s = settingsPageOf(route);
  if (s) return SETTINGS[s.key].parent;
  const [a = '', b] = route;
  // La séance et son plan (une seule fiche, deux segments) : le planning.
  if (a === 'seance' || (a === 'e' && b)) return { hash: '#/planning', label: 'Planning' };
  if (a === 'salles' && b) return { hash: '#/salles', label: 'Les salles' };
  if (a === 'prospection' && b === 'e') return { hash: '#/prospection/suivi', label: 'Prospection' };
  if (a === 'subventions' && b === 'dossier') return { hash: '#/subventions/dossiers', label: 'Subventions' };
  if (fallback) {
    const known = titleOfHash(fallback.hash);
    const label = /^retour$/i.test(fallback.label.trim()) ? known ?? 'Accueil' : fallback.label;
    return { hash: /^retour$/i.test(fallback.label.trim()) && !known ? '#/' : fallback.hash, label };
  }
  return { hash: '#/', label: 'Accueil' };
}

/** Le nom d'une page pour le retour de la suivante : court (le titre d'un écran peut être long). */
export function shortTitle(t: string, max = 30): string {
  const s = t.replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return `${(sp > 12 ? cut.slice(0, sp) : cut).replace(/[\s,;:·–-]+$/, '')}…`;
}
